import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { subdivisionesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, Building, Layers, Shield } from 'lucide-react';
import { Badge } from './ui/badge';

const TIPOS_SUBDIVISION = ['Bloque', 'Edificio', 'Etapa', 'Manzana', 'Zona'];

const SubdivisionesManager = ({ proyecto, onUpdate }) => {
  const [subdivisiones, setSubdivisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSubdivision, setEditingSubdivision] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [saving, setSaving] = useState(false);
  
  const initialFormData = {
    nombre: '',
    codigo: proyecto?.codigo_proyecto ? `${proyecto.codigo_proyecto}-` : '',
    tipo_subdivision: '',
    numero_pisos: '',
    observaciones: '',
    padre_id: '',
    fecha_inicio_garantia_acabados: '',
    garantia_acabados_anios: 1,
    garantia_acabados_meses: 0,
    fecha_inicio_garantia_estructural: '',
    garantia_estructural_anios: 10,
    garantia_estructural_meses: 0,
  };
  
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (proyecto?.id) {
      loadSubdivisiones();
    }
  }, [proyecto?.id]);

  const loadSubdivisiones = async () => {
    try {
      const response = await subdivisionesAPI.getByProyecto(proyecto.id);
      setSubdivisiones(response.data || []);
    } catch (error) {
      toast.error('Error al cargar subdivisiones');
    } finally {
      setLoading(false);
    }
  };

  const organizeSubdivisiones = () => {
    const roots = subdivisiones.filter(s => !s.padre_id);
    const children = subdivisiones.filter(s => s.padre_id);
    
    const buildTree = (items) => {
      return items.map(item => ({
        ...item,
        children: buildTree(children.filter(c => c.padre_id === item.id))
      }));
    };
    
    return buildTree(roots);
  };

  const handleOpenCreate = (padreId = null) => {
    setEditingSubdivision(null);
    setFormData({
      ...initialFormData,
      codigo: proyecto?.codigo_proyecto ? `${proyecto.codigo_proyecto}-` : '',
      padre_id: padreId || '',
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (subdivision) => {
    setEditingSubdivision(subdivision);
    setFormData({
      nombre: subdivision.nombre || '',
      codigo: subdivision.codigo || '',
      tipo_subdivision: subdivision.tipo_subdivision || '',
      numero_pisos: subdivision.numero_pisos || '',
      observaciones: subdivision.observaciones || '',
      padre_id: subdivision.padre_id || '',
      fecha_inicio_garantia_acabados: subdivision.fecha_inicio_garantia_acabados || '',
      garantia_acabados_anios: subdivision.garantia_acabados_anios ?? 1,
      garantia_acabados_meses: subdivision.garantia_acabados_meses ?? 0,
      fecha_inicio_garantia_estructural: subdivision.fecha_inicio_garantia_estructural || '',
      garantia_estructural_anios: subdivision.garantia_estructural_anios ?? 10,
      garantia_estructural_meses: subdivision.garantia_estructural_meses ?? 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.codigo.trim() || formData.codigo === `${proyecto?.codigo_proyecto}-`) {
      toast.error('El código es obligatorio');
      return;
    }
    if (!formData.tipo_subdivision) {
      toast.error('El tipo de subdivisión es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        proyecto_id: proyecto.id,
        numero_pisos: formData.numero_pisos ? parseInt(formData.numero_pisos) : null,
        padre_id: formData.padre_id || null,
        garantia_acabados_anios: parseInt(formData.garantia_acabados_anios),
        garantia_acabados_meses: parseInt(formData.garantia_acabados_meses),
        garantia_estructural_anios: parseInt(formData.garantia_estructural_anios),
        garantia_estructural_meses: parseInt(formData.garantia_estructural_meses),
      };

      if (editingSubdivision) {
        await subdivisionesAPI.update(editingSubdivision.id, data);
        toast.success('Subdivisión actualizada');
      } else {
        await subdivisionesAPI.create(proyecto.id, data);
        toast.success('Subdivisión creada');
      }

      setShowDialog(false);
      loadSubdivisiones();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta subdivisión?')) return;
    
    try {
      await subdivisionesAPI.delete(id);
      toast.success('Subdivisión eliminada');
      loadSubdivisiones();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatGarantia = (anios, meses) => {
    const parts = [];
    if (anios > 0) parts.push(`${anios}a`);
    if (meses > 0) parts.push(`${meses}m`);
    return parts.length > 0 ? parts.join(' ') : '0';
  };

  const renderSubdivision = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    
    return (
      <div key={item.id} className="mb-2">
        <div 
          className={`p-3 border border-[#E8E8E7] rounded-lg bg-white hover:shadow-sm transition-shadow`}
          style={{ marginLeft: `${depth * 20}px` }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {hasChildren ? (
                <button onClick={() => toggleExpand(item.id)} className="p-1 hover:bg-gray-100 rounded">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              ) : (
                <span className="w-6" />
              )}
              <Layers size={16} className="text-[#9d8272] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{item.nombre}</span>
                  <Badge variant="outline" className="text-[10px]">{item.tipo_subdivision}</Badge>
                  <span className="text-[10px] text-[#888888] font-mono">{item.codigo}</span>
                </div>
                {item.numero_pisos && (
                  <span className="text-[10px] text-[#888888]">{item.numero_pisos} pisos</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleOpenCreate(item.id)} className="h-7 w-7 p-0">
                <Plus size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)} className="h-7 w-7 p-0">
                <Edit size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {item.children.map(child => renderSubdivision(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const organizedSubdivisiones = organizeSubdivisiones();

  if (loading) {
    return <div className="text-center py-4 text-sm text-[#888888]">Cargando subdivisiones...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#2D2D2D] flex items-center gap-2">
          <Layers size={16} className="text-[#9d8272]" />
          Subdivisiones ({subdivisiones.length})
        </h3>
        <Button size="sm" onClick={() => handleOpenCreate()} className="h-8 bg-[#9d8272] hover:bg-[#8a7162] text-white">
          <Plus size={14} className="mr-1" />
          Agregar
        </Button>
      </div>

      {organizedSubdivisiones.length > 0 ? (
        <div className="space-y-1">
          {organizedSubdivisiones.map(item => renderSubdivision(item))}
        </div>
      ) : (
        <div className="text-center py-6 text-sm text-[#888888] bg-[#F5F5F5] rounded-lg">
          No hay subdivisiones registradas
        </div>
      )}

      {/* Dialog para crear/editar subdivisión */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D2D2D]">
              {editingSubdivision ? 'Editar Subdivisión' : 'Nueva Subdivisión'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Datos Básicos */}
            <div className="bg-[#F5F5F5] p-4 rounded-lg space-y-3">
              <Label className="text-sm font-semibold text-[#9d8272]">Datos Básicos</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value.slice(0, 30) })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                    maxLength={30}
                    placeholder="Nombre de la subdivisión"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Código * (máx 40 car.)</Label>
                  <Input
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.slice(0, 40) })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm font-mono"
                    maxLength={40}
                    placeholder={`${proyecto?.codigo_proyecto || 'CP'}-XXX`}
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Tipo de Subdivisión *</Label>
                  <Select value={formData.tipo_subdivision} onValueChange={(v) => setFormData({ ...formData, tipo_subdivision: v })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue placeholder="Seleccione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_SUBDIVISION.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Número de Pisos</Label>
                  <Input
                    type="number"
                    value={formData.numero_pisos}
                    onChange={(e) => setFormData({ ...formData, numero_pisos: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                    min="0"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Pertenece a</Label>
                  <Select value={formData.padre_id || "none"} onValueChange={(v) => setFormData({ ...formData, padre_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue placeholder="Ninguna (raíz)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguna (raíz)</SelectItem>
                      {subdivisiones
                        .filter(s => s.id !== editingSubdivision?.id)
                        .map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.codigo} - {s.nombre}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label className="text-xs">Observaciones</Label>
                  <Textarea
                    value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value.slice(0, 1000) })}
                    className="border-[#c5b8a2] mt-1 text-sm"
                    rows={2}
                    maxLength={1000}
                    placeholder="Observaciones adicionales..."
                  />
                </div>
              </div>
            </div>

            {/* Garantía */}
            <div className="bg-[#F5F5F5] p-4 rounded-lg space-y-3">
              <Label className="text-sm font-semibold text-[#9d8272] flex items-center gap-2">
                <Shield size={14} />
                Garantía
              </Label>
              
              {/* Garantía Acabados */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3">
                  <Label className="text-[10px] text-[#8c926f] font-medium">Garantía de Acabados</Label>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_acabados}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_acabados: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Años</Label>
                  <Select value={String(formData.garantia_acabados_anios)} onValueChange={(v) => setFormData({ ...formData, garantia_acabados_anios: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Meses</Label>
                  <Select value={String(formData.garantia_acabados_meses)} onValueChange={(v) => setFormData({ ...formData, garantia_acabados_meses: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Garantía Estructural */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3">
                  <Label className="text-[10px] text-[#9d8272] font-medium">Garantía Estructural</Label>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_estructural}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_estructural: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Años</Label>
                  <Select value={String(formData.garantia_estructural_anios)} onValueChange={(v) => setFormData({ ...formData, garantia_estructural_anios: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Meses</Label>
                  <Select value={String(formData.garantia_estructural_meses)} onValueChange={(v) => setFormData({ ...formData, garantia_estructural_meses: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} size="sm">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#9d8272] hover:bg-[#8a7162] text-white">
                {saving ? 'Guardando...' : editingSubdivision ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubdivisionesManager;
